<template>
  <div
    class="gw_table gw_table_body"
    :class="[
      { gw_table_body_loading: loading && !loadingBottom },
      { 'gw_table_scrolled-left': scrolledLeft },
    ]"
    @touchend="hscrl_handleTouchend"
  >
    <slot name="before"></slot>

    <slot name="loading">
      <div
        v-if="loading && !loadingBottom"
        class="gw_table_body_loader anim-fade-in"
      >
        <LoaderSpinner :style="`--size:var(--body-loader-size)`" />
      </div>
      <div
        v-if="loadingBottom"
        class="gw_table_body_loader_bottom anim-fade-in"
      >
        <LoaderSpinner :style="`--size:var(--bottom-loader-size)`" />
      </div>
    </slot>
    <slot name="error">
      <div
        v-if="error && !loading"
        class="gw_table_body_error anim-fade-in"
        :data-tst="`table_error_code_${error.status}`"
      >
        <div class="alert-box f-column">
          <div
            class="weight_700"
            v-html="$t('messages.oops_error')"
          ></div>
          <div
            class="text-grey"
            v-html="$t('messages.opps_error_sub')"
          ></div>
        </div>
      </div>
    </slot>

    <!-- :beforeStyle="beforeStyle" -->
    <!-- :hasDynamicSize="isInfiniteDynamic" -->
    <!-- {{ renderItems }} -->

    <slot
      v-if="!overflowWidth"
      name="head_before"
    ></slot>

    <VirtualList
      v-if="isInfinite || isInfiniteDynamic"
      ref="scroller"
      :hasDynamicSize="isInfiniteDynamic"
      :items="items"
      :class="scrollTrack"
      :estimateSize="itemSize"
      :itemSize="itemSize"
      :idKey="idKey"
      :keeps="renderItems"
      class="gw_table_body_scroller"
      @scroll="handleScroll"
      @visible="handleVisible"
    >
      <template #before>
        <slot name="head_sticky"></slot>
        <slot
          v-if="overflowWidth"
          name="head_before"
        ></slot>
      </template>
      <template #item="{ item, index }">
        <slot
          :item="item"
          :index="index"
          :size="itemSize"
        >
        </slot>
      </template>
      <template #after>
        <slot :name="!loading && !items.length && !error ? 'no_data' : 'none'"> </slot>
      </template>
    </VirtualList>

    <div
      v-else
      ref="scroller"
      class="gw_table_body_scroller"
      :class="scrollTrack"
      @scroll="handleScroll"
    >
      <div class="gw_table_body_scroller_root">
        <slot name="head_sticky"></slot>
        <slot
          v-if="overflowWidth"
          name="head_before"
        ></slot>

        <template
          v-for="(item, index) in items"
          :key="index"
        >
          <slot
            :item="item"
            :index="index"
          >
          </slot>
        </template>
      </div>
      <div
        v-if="!loading && !items.length && !error"
        class="gw_table_body_nodata"
      >
        <slot name="no_data"> </slot>
      </div>
    </div>

    <slot name="after"></slot>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner'
import VirtualList from '@/components/vue3/VirtualList/VirtualList'

export default {
  name: 'TableBody',
  components: {
    VirtualList,
    LoaderSpinner,
  },
  props: {
    loadingBottom: Boolean,
    error: Object,
    isInfinite: Boolean,
    isInfiniteDynamic: Boolean,
    items: Array,
    idKey: { default: 'id' },
    loading: Boolean,
    scrollTrack: { default: () => 'large-scroll-track' },
    // scrollTrack: { default: () => 'hover-scroll-track' },
    beforeStyle: { default: () => 'position: sticky; top:0; z-index:var(--z-index-head)' },
    size: { default: 'small' },
  },
  emits: ['update:visible', 'update:scrollX', 'update:scrollY', 'update:scroll'],
  data() {
    return {
      lastTouchendTime: null,
      hscrl_surface: null,
      virtualVisible: false,
      renderItems: 101,
      scrolledLeft: false,
      overflowWidth: false,
      overflowHeight: false,
    }
  },
  mounted() {
    nextTick(() => {
      this.getRenderItems()
      this.getOverflowWidth()
    })
  },
  methods: {
    handleVisible(e) {
      this.virtualVisible = true
      this.$emit('update:visible', e)
    },
    handleScroll(e) {
      this.scrolledLeft = e.target.scrollLeft > 0

      this.$emit('update:scrollX', e)
      this.$emit('update:scrollY', e)
      this.$emit('update:scroll', e, this.$refs.scroller?.$el || this.$refs.scroller)
    },
    hscrl_handleTouchend(e) {
      const scroller = this.$refs.scroller?.$el || this.$refs.scroller
      if (!scroller) return
      // add info on event for parent handling swipe events
      const timestamp = Date.now()
      const ist_top = scroller.scrollTop <= 0
      const ist_bottom = scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight
      const ist_left = scroller.scrollLeft <= 0
      const ist_right = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth

      const ist_top_time =
        this.hscrl_surface?.ist_to === ist_top ? this.hscrl_surface.ist_top_time || 0 : timestamp
      const ist_bottom_time =
        this.hscrl_surface?.ist_bottom === ist_bottom
          ? this.hscrl_surface.ist_bottom_time || 0
          : timestamp
      const ist_left_time =
        this.hscrl_surface?.ist_left === ist_left
          ? this.hscrl_surface.ist_left_time || 0
          : timestamp
      const ist_right_time =
        this.hscrl_surface?.ist_right === ist_right
          ? this.hscrl_surface.ist_right_time || 0
          : timestamp

      const surface = {
        timestamp,
        ist_first: !this.hscrl_surface,
        ist_top,
        ist_bottom,
        ist_left,
        ist_right,
        ist_top_time,
        ist_bottom_time,
        ist_left_time,
        ist_right_time,
      }

      this.hscrl_surface = surface
      e.gw_touchends = [...(e.gw_touchends || []), surface]
    },
    resetScrollLeft() {
      if (this.$refs.scroller?.$el) {
        this.$refs.scroller.$el.scrollLeft = 0
      } else if (this.$refs.scroller) {
        this.$refs.scroller.scrollLeft = 0
      }
    },
    scrollTop(num = 0) {
      if (this.$refs.scroller?.$el) {
        this.$refs.scroller.$el.scrollTop = num
      } else if (this.$refs.scroller) {
        this.$refs.scroller.scrollTop = 0
      }
    },
    scrollBy(num = 0) {
      if (this.$refs.scroller?.$el) {
        this.$refs.scroller.$el.scrollTop += num
      } else if (this.$refs.scroller) {
        this.$refs.scroller.scrollTop += num
      }
    },
    getOverflowWidth() {
      if (!this.$refs?.scroller) return false
      const scrollWidth =
        this.$refs?.scroller?.scrollWidth || this.$refs?.scroller?.$el?.scrollWidth
      const clientWidth =
        this.$refs?.scroller?.clientWidth || this.$refs?.scroller?.$el?.clientWidth
      this.overflowWidth = scrollWidth > clientWidth
      return this.overflowWidth
    },
    getOverflowHeight() {
      if (!this.$refs?.scroller) return false
      // called from parent (reptable)
      const scrollHeight =
        this.$refs?.scroller?.scrollHeight || this.$refs?.scroller?.$el?.scrollHeight
      const clientHeight =
        this.$refs?.scroller?.clientHeight || this.$refs?.scroller?.$el?.clientHeight
      this.overflowHeight = scrollHeight > clientHeight
      return this.overflowHeight
    },
    getRenderItems() {
      if (!this.isInfinite) return
      const cont = this.$refs?.scroller?.$el
      if (cont) {
        const res = Math.floor(cont.clientHeight / this.itemSize) * 3
        this.renderItems = res < 50 ? 50 : res || 50 // less than 50 on smaller screen sizes for some reaons it dont render all items on scroll
      }
    },
  },
  computed: {
    itemSize() {
      if (this.globals_window_size) {
        const fntSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize)
        const sizes = {
          large: 3,
          medium: 2.5,
          small: 2,
        }
        const res = fntSizePx * sizes[this.size]
        return res
      }
      return 30
    },
    globals_window_size() {
      return this.$store.getters.globals_window_size
    },
  },
  watch: {
    items() {
      if (this.loading) return
      nextTick(() => {
        this.getRenderItems()
        this.getOverflowWidth()
        this.resetScrollLeft()
      })
    },
    globals_window_size() {
      setTimeout(() => {
        this.getRenderItems()
        this.getOverflowWidth()
      }, 111)
    },
  },
}
</script>
